import { useState, useEffect, useCallback, useRef } from "react";

interface LegacyChatCheckResult {
  hasLegacyChats: boolean;
  chatCount: number;
  status: string;
  worldviaUserId: string | null;
  alreadyImportedCount?: number;
}

interface LegacyChatImportStatus {
  status: string;
  importCount: number;
  importedAt: string | null;
  hasWorldviaId: boolean;
  canImport: boolean;
}

interface UseLegacyChatImportOptions {
  /** Only check once per session */
  checkOnce?: boolean;
  /** Delay before checking (ms) */
  checkDelay?: number;
  /** Callback when import is successful - use to refresh data */
  onImportSuccess?: () => void;
}

interface UseLegacyChatImportReturn {
  /** Whether the import modal should be shown */
  showImportModal: boolean;
  /** Whether manual recheck mode is active */
  isManualRecheck: boolean;
  /** Open the import modal */
  openImportModal: () => void;
  /** Open import modal with forced recheck (for manual trigger from settings) */
  openImportModalForRecheck: () => void;
  /** Close the import modal - conforms to Dialog's onOpenChange interface */
  closeImportModal: (open: boolean) => void;
  /** Check result from the API */
  checkResult: LegacyChatCheckResult | null;
  /** Whether we're currently checking */
  isChecking: boolean;
  /** Import status info */
  importStatus: LegacyChatImportStatus | null;
  /** Manually trigger a check */
  checkForLegacyChats: () => Promise<void>;
  /** Fetch current import status */
  fetchImportStatus: () => Promise<void>;
  /** Called when import is complete (user dismissed success screen) */
  handleImportComplete: () => void;
}

const STORAGE_KEY = "aivia_legacy_chat_checked";

export function useLegacyChatImport(
  options: UseLegacyChatImportOptions = {}
): UseLegacyChatImportReturn {
  const { checkOnce = true, checkDelay = 1500, onImportSuccess } = options;

  const [showImportModal, setShowImportModal] = useState(false);
  const [isManualRecheck, setIsManualRecheck] = useState(false);
  const [checkResult, setCheckResult] = useState<LegacyChatCheckResult | null>(null);
  const [importStatus, setImportStatus] = useState<LegacyChatImportStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  
  // Use ref instead of state for hasChecked to avoid dependency cycle
  // This tracks whether we've already run the check in this component instance
  const hasCheckedRef = useRef(false);

  const checkForLegacyChats = useCallback(async () => {
    // Skip if already checked this session (ref check - no re-render)
    if (checkOnce && hasCheckedRef.current) {
      return;
    }

    // Check sessionStorage to avoid repeated checks across page loads
    if (checkOnce && typeof sessionStorage !== 'undefined') {
      const alreadyChecked = sessionStorage.getItem(STORAGE_KEY);
      if (alreadyChecked === "true") {
        hasCheckedRef.current = true;
        return;
      }
    }

    setIsChecking(true);

    try {
      const response = await fetch("/api/legacy-chat-import/check", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to check for legacy chats");
      }

      const result: LegacyChatCheckResult = await response.json();
      setCheckResult(result);

      // Only show modal if user has legacy chats and hasn't imported/declined yet
      if (result.hasLegacyChats && result.status === "available") {
        setShowImportModal(true);
      }

      // Mark as checked (ref for this instance, sessionStorage for persistence)
      hasCheckedRef.current = true;
      if (checkOnce && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(STORAGE_KEY, "true");
      }
    } catch {
      // Silently fail - this is a non-critical background check
    } finally {
      setIsChecking(false);
    }
  }, [checkOnce]); // Note: hasCheckedRef is a ref, not in deps - this is intentional

  const fetchImportStatus = useCallback(async () => {
    try {
      const response = await fetch("/api/legacy-chat-import/status", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch import status");
      }

      const result: LegacyChatImportStatus = await response.json();
      setImportStatus(result);
    } catch {
      // Silently fail - status fetch is non-critical
    }
  }, []);

  // Auto-check on mount with delay
  useEffect(() => {
    const timer = setTimeout(() => {
      checkForLegacyChats();
    }, checkDelay);

    return () => clearTimeout(timer);
  }, [checkDelay, checkForLegacyChats]);

  const openImportModal = useCallback(() => {
    setIsManualRecheck(false);
    setShowImportModal(true);
  }, []);

  /** Open import modal with forced recheck - used for manual trigger from user menu */
  const openImportModalForRecheck = useCallback(() => {
    // Clear ALL session check guards so manual rechecks can proceed
    // This allows subsequent calls to checkForLegacyChats to work
    hasCheckedRef.current = false;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    setIsManualRecheck(true);
    setShowImportModal(true);
  }, []);

  // Conforms to (open: boolean) => void interface for use as Dialog's onOpenChange
  // Only closes when open=false; ignores open=true (opening is handled by openImportModal*)
  const closeImportModal = useCallback((open: boolean) => {
    if (!open) {
      setShowImportModal(false);
      setIsManualRecheck(false);
    }
  }, []);

  const handleImportComplete = useCallback(() => {
    setShowImportModal(false);
    setIsManualRecheck(false);
    // Clear the session check so it doesn't show again
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY, "true");
    }
    // Refresh import status
    fetchImportStatus();
    // Call the success callback to refresh data (e.g., sessions list)
    onImportSuccess?.();
  }, [fetchImportStatus, onImportSuccess]);

  return {
    showImportModal,
    isManualRecheck,
    openImportModal,
    openImportModalForRecheck,
    closeImportModal,
    checkResult,
    isChecking,
    importStatus,
    checkForLegacyChats,
    fetchImportStatus,
    handleImportComplete,
  };
}
